/**
 * ContextStore — active tenant + agent context for the admin panel.
 *
 * Design principles:
 *  1. Single source of truth for which tenant/agent is "active".
 *  2. Every write also calls requestContext so Axios headers stay in sync.
 *  3. Switching tenant always clears agent (prevents cross-tenant leakage).
 *  4. Persisted to localStorage (tenant/agent IDs only) so context survives
 *     page refreshes.
 *  5. Full tenant/agent objects are stored in memory only — re-fetched after
 *     rehydration via the `hydrate` action.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { requestContext } from '@core/api/requestContext';
import type { Tenant } from '@/services/tenant.service';
import type { Agent } from '@/services/agent.service';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ContextState {
  /** ID of the currently active tenant — included in every API request */
  activeTenantId: string | null;
  /** Full tenant object (in-memory, not persisted — may be null on first load) */
  activeTenant:   Tenant | null;

  /** ID of the currently active agent — included in scoped API requests */
  activeAgentId: string | null;
  /** Full agent object (in-memory only) */
  activeAgent:   Agent | null;

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Switch the active tenant.
   * ⚠️ Clears activeAgent — an agent belongs to a specific tenant.
   */
  setActiveTenant: (tenant: Tenant) => void;

  /**
   * Restore the active tenant object without clearing agent context.
   * Used after page refresh when IDs are already persisted.
   */
  restoreActiveTenant: (tenant: Tenant) => void;

  /**
   * Set the active agent (must belong to the current active tenant).
   * Guards against setting an agent from a different tenant.
   */
  setActiveAgent: (agent: Agent) => void;

  /**
   * Set the active tenant by id only (no full object) — used for tenant-scoped
   * users whose tenant comes from their token and who can't list /tenants.
   */
  setActiveTenantId: (tenantId: string | null) => void;

  /** Clear agent context without changing tenant */
  clearActiveAgent: () => void;

  /** Full reset — called on logout */
  clearAll: () => void;

  /**
   * Restore full objects from IDs after page rehydration.
   * Call this once on app start with freshly-fetched data.
   */
  hydrateObjects: (tenant: Tenant | null, agent: Agent | null) => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      activeTenantId: null,
      activeTenant:   null,
      activeAgentId:  null,
      activeAgent:    null,

      // ── setActiveTenant ────────────────────────────────────────────────────

      setActiveTenant: (tenant) => {
        // 1. Update requestContext so the Axios interceptor picks it up
        requestContext.switchTenant(tenant.id); // also clears agentId

        // 2. Update Zustand state (agent cleared on tenant switch)
        set({
          activeTenantId: tenant.id,
          activeTenant:   tenant,
          activeAgentId:  null,
          activeAgent:    null,
        });
      },

      // ── restoreActiveTenant ────────────────────────────────────────────────

      restoreActiveTenant: (tenant) => {
        const { activeTenantId, activeAgentId } = get();

        if (activeTenantId !== tenant.id) {
          get().setActiveTenant(tenant);
          return;
        }

        requestContext.setTenantId(tenant.id);
        if (activeAgentId) {
          requestContext.setAgentId(activeAgentId);
        }
        set({ activeTenant: tenant });
      },

      // ── setActiveAgent ─────────────────────────────────────────────────────

      setActiveAgent: (agent) => {
        const { activeTenantId } = get();

        // Guard: agent must belong to the active tenant
        if (activeTenantId && agent.tenantId !== activeTenantId) {
          console.error(
            `[ContextStore] Cross-tenant agent rejected: ` +
            `agent.tenantId=${agent.tenantId}, activeTenantId=${activeTenantId}`,
          );
          return;
        }

        requestContext.setAgentId(agent.id);
        set({ activeAgentId: agent.id, activeAgent: agent });
      },

      // ── setActiveTenantId ──────────────────────────────────────────────────

      setActiveTenantId: (tenantId) => {
        if (tenantId) {
          requestContext.switchTenant(tenantId);
          set({
            activeTenantId: tenantId,
            activeTenant: null,
            activeAgentId: null,
            activeAgent: null,
          });
        } else {
          requestContext.setTenantId(null);
          set({ activeTenantId: null, activeTenant: null });
        }
      },

      // ── clearActiveAgent ───────────────────────────────────────────────────

      clearActiveAgent: () => {
        requestContext.setAgentId(null);
        set({ activeAgentId: null, activeAgent: null });
      },

      // ── clearAll ──────────────────────────────────────────────────────────

      clearAll: () => {
        requestContext.clear();
        set({
          activeTenantId: null,
          activeTenant:   null,
          activeAgentId:  null,
          activeAgent:    null,
        });
      },

      // ── hydrateObjects ────────────────────────────────────────────────────

      hydrateObjects: (tenant, agent) => {
        set({
          activeTenant: tenant,
          activeAgent:  agent,
        });
      },
    }),

    {
      name: 'ai-copilot-context',

      // Persist IDs only — full objects are re-fetched after rehydration
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        activeAgentId:  state.activeAgentId,
      }),

      // After rehydration: sync persisted IDs back into requestContext so the
      // VERY FIRST API request uses the correct headers
      onRehydrateStorage: () => (state) => {
        if (state?.activeTenantId) {
          requestContext.setTenantId(state.activeTenantId);
        }
        if (state?.activeAgentId) {
          requestContext.setAgentId(state.activeAgentId);
        }
      },
    },
  ),
);
